import ApiError from 'api/ApiError'
import { constants as userConstants, USER_SESSION_EXPIRED } from 'store/modules/User'
import {
  actions as authActions
} from 'store/modules/Auth'
import { constants as tokenConstants, removeAccessToken, removeRefreshToken } from 'utils/authUtils'
import * as api from 'api/Api'
import { routerActions } from 'connected-react-router'
import { PATHS } from 'routes'

let fetchQueue = {}
// Shared in-flight refresh-token promise. When multiple requests fail with a 401
// concurrently, they must all await the SAME token refresh instead of each firing
// its own call to /getAccessToken - concurrent refresh calls race against each
// other (refresh token rotation) and result in repeated calls with a stale token
// and duplicate rows being created in nc_access_token.
let refreshTokenPromise = null

/**
 * This function extends isomorphic-fetch:
 * [1] access state and dispatch actions
 * [2] handles session expiry
 * [3] avoid multiple invocation of fetch before initial/previous promise is resolved
 *
 * @param {object} options
 * @param {function} dispatch
 * @param {function} getState
 *
 * @return {promise|undefined}
 */
const genericFetch = async (options, dispatch, getState) => {
  // AbortController & AbortSignal was not supported by all browser at the time of development
  // So, timestamp created at the recent invocation of method is used as reference
  // to ignore the success/failire of previous api call
  let timestamp
  if (options.preventMultipleRequest) {
    timestamp = +(new Date())
    fetchQueue[options.apiFunction] = timestamp
  }

  if (options.onFetch) await dispatch(options.onFetch())
  try {
    let arg = typeof options.args === 'function' ? options.args(getState) : options.args
    if (options.callBackFunc) {
      arg = { ...arg, ...{ callBackFunc: options.callBackFunc } }
    }
    const res = await api[options.apiFunction](arg)

    // success callback should be only be invoked for recent request
    if (options.onSuccess) {
      if (!options.preventMultipleRequest ||
        (options.preventMultipleRequest && fetchQueue[options.apiFunction] === timestamp)) {
        await dispatch(options.onSuccess(res, getState))
      }
    }

    if (options.getResponse) return res
  } catch (error) {
    if (error instanceof ApiError) {
      // NOTE: this branch is intentionally not wrapped in react-redux `batch`.
      // `batch` ignores the promise returned by an async callback, so awaited
      // work (and a rethrown `throwError`) would escape it as an unhandled
      // rejection. Awaiting directly keeps ordering and error propagation intact.
      if (options.apiFunction === 'getAccessToken') {
        removeAccessToken()
        removeRefreshToken()
        await dispatch({ type: userConstants.FETCH_FAILURE, error: USER_SESSION_EXPIRED })
        await dispatch(routerActions.replace(PATHS.LOGIN))
      } else if (error.status === 401 && !options.preventRetryAccessToken && options.apiFunction !== 'changePassword') {
        if (localStorage.getItem(tokenConstants.REFRESH_TOKEN)) {
          // De-duplicate concurrent token refresh attempts: if a refresh is already
          // in-flight, reuse its promise instead of dispatching another refresh call.
          if (!refreshTokenPromise) {
            refreshTokenPromise = dispatch(authActions.getAccessToken())
              .finally(() => {
                refreshTokenPromise = null
              })
          }
          await refreshTokenPromise
          // Retry only once: disable further refreshes so a request that keeps
          // returning 401 after a successful refresh (stale/rejected session)
          // cannot loop indefinitely.
          await genericFetch({ ...options, preventRetryAccessToken: true }, dispatch, getState)
        } else {
          // No refresh token: the session cannot be recovered. The stale access token
          // must be cleared here, otherwise the login/landing screens bounce straight
          // back to /dashboard and every remount re-fires the failing request.
          removeAccessToken()
          removeRefreshToken()
          await dispatch({ type: userConstants.FETCH_FAILURE, error: USER_SESSION_EXPIRED, errorMessage: error })
          await dispatch(routerActions.replace(PATHS.LOGIN))
        }
      } else {
        if (options.onFailure) {
          // error handling is not required for old request
          if (!options.preventMultipleRequest ||
            (options.preventMultipleRequest && fetchQueue[options.apiFunction] === timestamp)) {
            await dispatch(options.onFailure(error))
          }
        }
        if (options.throwError) throw error
      }
    }
  }
}

export default (options = {}) => async (dispatch, getState) => genericFetch(options, dispatch, getState)